import { useId, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus, FolderPlus } from "lucide-react";
import type { BuildingProject } from "@/types/projects";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { usePermissions } from "@/hooks/usePermissions";
import {
  ProjectCreateFieldLabel,
  ProjectStructureGuidance,
} from "@/domains/projects/components/ProjectCreateFormPresentation";
import { useProjects } from "../hooks/useProjects";
import { ProjectStatusBadge } from "@/domains/projects/status/ProjectStatusBadge";
import { ProjectSelectContent } from "@/domains/projects/status/ProjectSelectContent";
import { useProjectStatusFilter } from "@/domains/projects/status/useProjectStatusFilter";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface ProjectSelectCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  buildingId: string;
  onProjectSelect: (project: BuildingProject) => void;
}

export function ProjectSelectCreateModal({
  isOpen,
  onClose,
  onProjectSelect,
  buildingId,
}: ProjectSelectCreateModalProps) {
  const { t } = useLanguage();
  const { canCreateProjects } = usePermissions();
  const { projects, isLoading, createProject } = useProjects(buildingId);

  const [mode, setMode] = useState<"select" | "create">("select");
  const [newProjectName, setNewProjectName] = useState("");
  const [newProjectDescription, setNewProjectDescription] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");
  const [isCreating, setIsCreating] = useState(false);
  const fieldId = useId();
  const nameId = `${fieldId}-project-name`;
  const descriptionId = `${fieldId}-project-description`;

  // Get the currently selected project
  const selectedProject = projects.find((p) => p.id === selectedProjectId);
  const statusFilter = useProjectStatusFilter(projects, selectedProjectId || null);

  const handleProjectSelect = () => {
    if (!selectedProject) return;
    onProjectSelect(selectedProject);
    onClose();
  };

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) {
      toast.error(t.projects.create.nameRequired);
      return;
    }

    setIsCreating(true);

    createProject(
      {
        building_id: buildingId,
        name: newProjectName.trim(),
        description: newProjectDescription.trim() || undefined,
      },
      {
        // The success/error toasts are owned by useProjects' create mutation
        // (single toast source) — don't fire them again here, or the user sees
        // two stacked toasts for one project creation.
        onSuccess: (result) => {
          if (result) {
            // Select the newly created project (convert to string for consistency)
            setSelectedProjectId(String(result.id));
            setMode("select");
            // Reset form
            setNewProjectName("");
            setNewProjectDescription("");
          }
          setIsCreating(false);
        },
        onError: (error) => {
          console.error("Failed to create project:", error);
          setIsCreating(false);
        },
      }
    );
  };

  const handleModeSwitch = (newMode: "select" | "create") => {
    setMode(newMode);
    setSelectedProjectId("");
    setNewProjectName("");
    setNewProjectDescription("");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? t.projects.create.modalTitle : t.projects.selectModal.title}
          </DialogTitle>
          <DialogDescription>
            {mode === "create"
              ? t.projects.create.modalSubtitle
              : t.projects.selectModal.chooseProject}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Mode selector */}
          <div className="flex gap-2">
            <Button
              variant={mode === "select" ? "default" : "outline"}
              size="sm"
              onClick={() => handleModeSwitch("select")}
              disabled={projects.length === 0}
            >
              {t.projects.selectModal.selectExisting}
            </Button>
            {canCreateProjects && (
              <Button
                variant={mode === "create" ? "default" : "outline"}
                size="sm"
                onClick={() => handleModeSwitch("create")}
              >
                <Plus className="mr-2 h-4 w-4" />
                {t.projects.selectModal.createNew}
              </Button>
            )}
          </div>

          {mode === "select" ? (
            <div className="space-y-4">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                  <span className="ml-2">{t.projects.selectModal.loading}</span>
                </div>
              ) : projects.length === 0 ? (
                <div className="py-6 text-center text-muted-foreground">
                  <FolderPlus className="mx-auto mb-2 h-12 w-12 opacity-50" />
                  <p>{t.projects.selectModal.noProjects}</p>
                  <p className="text-sm">{t.projects.selectModal.createFirst}</p>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label>{t.projects.selectModal.selectProject}</Label>
                    <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                      <SelectTrigger>
                        <SelectValue placeholder={t.projects.selectModal.chooseProject} />
                      </SelectTrigger>
                      <ProjectSelectContent statusFilter={statusFilter}>
                        {/* Every project stays mounted — see isShown. */}
                        {statusFilter.ordered.map((project) => (
                          <SelectItem
                            key={project.id}
                            value={project.id}
                            disabled={!statusFilter.isShown(project)}
                            className={cn(!statusFilter.isShown(project) && "hidden")}
                          >
                            <div>
                              <div className="flex items-center gap-1.5 font-medium">
                                <ProjectStatusBadge variant="dot" status={project.status} />
                                {project.name}
                              </div>
                              {project.description && (
                                <div className="text-xs text-muted-foreground">
                                  {project.description}
                                </div>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </ProjectSelectContent>
                    </Select>
                  </div>

                  <Button
                    onClick={handleProjectSelect}
                    disabled={!selectedProjectId}
                    className="w-full"
                  >
                    {t.projects.selectModal.selectProject}
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <ProjectCreateFieldLabel
                  htmlFor={nameId}
                  label={t.projects.create.modalNameLabel}
                />
                <Input
                  id={nameId}
                  aria-required="true"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  placeholder={t.projects.create.modalNamePlaceholder}
                />
              </div>

              <div className="space-y-1.5">
                <ProjectCreateFieldLabel
                  htmlFor={descriptionId}
                  label={t.projects.create.modalDescriptionLabel}
                  optionalHint={t.projects.create.modalOptionalLabel}
                />
                <Textarea
                  id={descriptionId}
                  value={newProjectDescription}
                  onChange={(e) => setNewProjectDescription(e.target.value)}
                  placeholder={t.projects.create.modalDescriptionPlaceholder}
                  rows={3}
                />
              </div>

              <ProjectStructureGuidance>{t.projects.create.modalGuidance}</ProjectStructureGuidance>

              <Button
                onClick={handleCreateProject}
                disabled={!newProjectName.trim() || isCreating}
                className="w-full"
              >
                {isCreating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t.projects.create.creating}
                  </>
                ) : (
                  <>
                    <Plus className="mr-2 h-4 w-4" />
                    {t.projects.create.modalSubmit}
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
